export interface ApiResponse<T> {
  ok: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined | null>;
  _retry?: boolean;
}

export class HttpClient {
  private baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl =
      baseUrl ||
      (import.meta.env?.VITE_API_URL as string) ||
      'http://localhost:8080';
  }

  public getBaseUrl(): string {
    return this.baseUrl;
  }

  public getToken(): string | null {
    return (
      localStorage.getItem('accessToken') ||
      localStorage.getItem('token') ||
      localStorage.getItem('insightnode_token')
    );
  }

  public getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  public setTokens(accessToken: string, refreshToken?: string): void {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('insightnode_token', accessToken);
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
  }

  public clearTokens(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('token');
    localStorage.removeItem('insightnode_token');
    localStorage.removeItem('refreshToken');
  }

  private redirectToLogin(): void {
    if (typeof window === 'undefined') return;
    if (!window.location.hash.includes('/login')) {
      window.location.hash = '#/login';
    }
  }

  private async refreshAccessToken(): Promise<string | null> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return null;

    try {
      const response = await fetch(`${this.baseUrl.replace(/\/$/, '')}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        this.clearTokens();
        return null;
      }

      const body = await response.json();
      const data = body.data || body;
      const newAccessToken = data.accessToken || data.token;
      const newRefreshToken = data.refreshToken;

      if (newAccessToken) {
        this.setTokens(newAccessToken, newRefreshToken);
        return newAccessToken;
      } else {
        this.clearTokens();
        return null;
      }
    } catch {
      this.clearTokens();
      return null;
    }
  }

  public async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { params, _retry, headers: customHeaders, ...fetchOptions } = options;

    let url = endpoint.startsWith('http')
      ? endpoint
      : `${this.baseUrl.replace(/\/$/, '')}/${endpoint.replace(/^\//, '')}`;

    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += (url.includes('?') ? '&' : '?') + queryString;
      }
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((customHeaders as Record<string, string>) || {}),
    };

    const token = this.getToken();
    if (token && !headers['Authorization']) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...fetchOptions,
      headers,
    });

    if (
      response.status === 401 &&
      !_retry &&
      !endpoint.includes('/auth/refresh') &&
      !endpoint.includes('/auth/login')
    ) {
      const newToken = await this.refreshAccessToken();
      if (newToken) {
        return this.request<T>(endpoint, { ...options, _retry: true });
      }
      this.clearTokens();
      this.redirectToLogin();
    }

    const contentType = response.headers.get('content-type');
    let body: any = null;
    if (response.status === 204) {
      body = null;
    } else if (contentType && contentType.includes('application/json')) {
      const text = await response.text();
      body = text ? JSON.parse(text) : null;
    } else {
      body = await response.text();
    }

    if (!response.ok) {
      const errorObj =
        body && typeof body === 'object' && body.error
          ? body.error
          : {
              code: `HTTP_${response.status}`,
              message:
                (typeof body === 'object' && body?.message) ||
                response.statusText ||
                'Request failed',
            };
      const error = new Error(errorObj.message || 'Request failed') as any;
      error.code = errorObj.code;
      error.status = response.status;
      error.details = errorObj.details;
      throw error;
    }

    if (body && typeof body === 'object' && 'ok' in body && body.ok === false) {
      const error = new Error(body.error?.message || 'API operation failed') as any;
      error.code = body.error?.code;
      throw error;
    }

    // Unwrap envelope response if applicable
    if (body && typeof body === 'object' && 'ok' in body && 'data' in body) {
      if ('total' in body || 'page' in body) {
        return {
          data: body.data,
          total: body.total,
          page: body.page,
          pageSize: body.pageSize,
          totalPages: body.totalPages,
        } as T;
      }
      return body.data as T;
    }

    return body as T;
  }

  public async fetchRaw(endpoint: string, options: RequestOptions = {}): Promise<Response> {
    const { params, _retry, headers: customHeaders, ...fetchOptions } = options;

    let url = endpoint.startsWith('http')
      ? endpoint
      : `${this.baseUrl.replace(/\/$/, '')}/${endpoint.replace(/^\//, '')}`;

    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += (url.includes('?') ? '&' : '?') + queryString;
      }
    }

    const headers: Record<string, string> = {
      ...((customHeaders as Record<string, string>) || {}),
    };

    const token = this.getToken();
    if (token && !headers['Authorization']) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      cache: 'no-store',
      ...fetchOptions,
      method: fetchOptions.method || 'GET',
      headers,
    });

    if (
      response.status === 401 &&
      !_retry &&
      !endpoint.includes('/auth/refresh') &&
      !endpoint.includes('/auth/login')
    ) {
      const newToken = await this.refreshAccessToken();
      if (newToken) {
        return this.fetchRaw(endpoint, { ...options, _retry: true });
      }
      this.clearTokens();
      this.redirectToLogin();
    }

    return response;
  }

  public async getBlob(endpoint: string, options: RequestOptions = {}): Promise<Blob> {
    const response = await this.fetchRaw(endpoint, options);
    if (!response.ok) {
      let message = response.statusText || 'Download failed';
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        try {
          const body = await response.json();
          message = body?.error?.message || body?.message || message;
        } catch {
          /* keep status text */
        }
      }
      const error = new Error(message) as any;
      error.status = response.status;
      throw error;
    }
    return response.blob();
  }

  public get<T>(
    endpoint: string,
    params?: Record<string, any>,
    options?: RequestOptions
  ): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', params, ...options });
  }

  public post<T>(
    endpoint: string,
    body?: any,
    options?: RequestOptions
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...options,
    });
  }

  public put<T>(
    endpoint: string,
    body?: any,
    options?: RequestOptions
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...options,
    });
  }

  public patch<T>(
    endpoint: string,
    body?: any,
    options?: RequestOptions
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...options,
    });
  }

  public delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE', ...options });
  }
}

export const api = new HttpClient();
